import { Section } from '../types';
import { ReportData } from '../types';
import { RenderableSection } from './types';
import { PageManager } from './PageManager';
import { splitHtmlContent } from './split-utils';
import {
    SECTION_GAP,
    CONTENT_WIDTH,
    DEFAULT_SECTION_HEIGHT,
    DEFAULT_TITLE_HEIGHT,
} from './constants';

interface ProcessTextOptions {
    section: Section;
    content: string;
    secId: string;
    measuredHeight: number | undefined;
    pageManager: PageManager;
    measurements: Record<string, number>;
    data: ReportData;
}

export function processTextSection({
    section,
    content,
    secId,
    measuredHeight,
    pageManager,
    measurements,
    data,
}: ProcessTextOptions) {
    const isFirstChunk = secId === section.id;
    const titleHeight = measurements[`section-${section.id}-title`] || DEFAULT_TITLE_HEIGHT;
    let h = measuredHeight;
    if (h === undefined) {
        h = measurements[`section-${section.id}`] || DEFAULT_SECTION_HEIGHT;
    }

    const remainingSpace = pageManager.getRemainingSpace();
    const availableContentHeight = Math.max(remainingSpace - (isFirstChunk ? titleHeight : 0), 0);

    if (h <= remainingSpace) {
        pageManager.recordPage(section.id);

        pageManager.addToPage({
            ...section,
            key: secId,
            contentOverride: content,
            isSplit: !isFirstChunk,
            hideTitle: !isFirstChunk,
        } as RenderableSection);

        pageManager.addHeight(h + SECTION_GAP);
        return;
    }

    // Case 2: Doesn't fit
    const isFreshPage = pageManager.isFreshPage();

    if (availableContentHeight <= 0 && !isFreshPage) {
        pageManager.startNewPage();
        processTextSection({
            section,
            content,
            secId,
            measuredHeight: h,
            pageManager,
            measurements,
            data,
        });
        return;
    }

    // Try to split
    const splitResult = splitHtmlContent(content, availableContentHeight, {
        containerWidth: CONTENT_WIDTH,
        fontConfig: data.customizations?.font,
        allowOversizeMediaScaling: isFreshPage,
    });

    if (splitResult.pending && !isFreshPage) {
        pageManager.startNewPage();
        processTextSection({
            section,
            content,
            secId,
            measuredHeight: h,
            pageManager,
            measurements,
            data,
        });
        return;
    }

    if (splitResult.fitted && splitResult.fittedHeight > 0) {
        if (!splitResult.remaining) {
            pageManager.recordPage(section.id);
            pageManager.addToPage({
                ...section,
                key: secId,
                contentOverride: splitResult.fitted,
                isSplit: !isFirstChunk,
                hideTitle: !isFirstChunk,
            } as RenderableSection);

            pageManager.addHeight(
                splitResult.fittedHeight + (isFirstChunk ? titleHeight : 0) + SECTION_GAP,
            );
            return;
        }

        // Add fitted part
        pageManager.recordPage(section.id);

        pageManager.addToPage({
            ...section,
            key: `${secId}-p${pageManager.currentPageIndex}`,
            contentOverride: splitResult.fitted,
            isSplit: !isFirstChunk,
            hideTitle: !isFirstChunk,
        } as RenderableSection);

        // Move to next page
        pageManager.startNewPage();

        // Process remaining recursively
        if (splitResult.remaining) {
            processTextSection({
                section,
                content: splitResult.remaining,
                secId: `${secId}-next`,
                measuredHeight: splitResult.remainingHeight,
                pageManager,
                measurements,
                data,
            });
        }
    } else {
        // Could not split effectively
        if (isFreshPage) {
            // If it's a fresh page and still doesn't fit (or split failed), force place it
            pageManager.recordPage(section.id);

            pageManager.addToPage({
                ...section,
                key: secId,
                contentOverride: content,
                isSplit: !isFirstChunk,
                hideTitle: !isFirstChunk,
            } as RenderableSection);

            pageManager.addHeight(h + SECTION_GAP);
        } else {
            // Push to new page and retry
            pageManager.startNewPage();
            processTextSection({
                section,
                content,
                secId,
                measuredHeight: h, // Use original height
                pageManager,
                measurements,
                data,
            });
        }
    }
}
